import { useCallback, useMemo } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ProfileClient } from '@atlaskit/profilecard';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl';
import {
	createUserProfileCardClient,
	getSpaActions,
	VIEW_PROFILE_ACTION,
} from '@atlassian/jira-profilecard-next';
import { useRouterActions } from '@atlassian/jira-router';
import {
	useCloudId,
	useFabricProfileDirectoryUrl,
	useTeamCentralUrl,
	useTeamCentralBaseUrl,
} from '@atlassian/jira-tenant-context-controller';
import { DEFAULT_PROFILE_DIR_URL, GATEWAY_GRAPHQL_URL } from './constants';
import type { ProfileCardProvider, Props } from './types';

const defaultActions = [VIEW_PROFILE_ACTION];

// TODO: Remove once profilecard-next's usage of baseUrl is also removed
const baseUrl = '';

export const useProfileCardProvider = (
	props: Partial<Props> = {},
): Promise<ProfileCardProvider> => {
	const { onLoadError: providedOnLoadError, actions = defaultActions } = props;

	const cloudId = useCloudId();
	const fabricPfDirUrl = useFabricProfileDirectoryUrl();

	const teamCentralUrl = useTeamCentralUrl();
	const teamCentralBaseUrl = useTeamCentralBaseUrl();

	const intl = useIntl();
	const { push } = useRouterActions();

	const onLoadError = useCallback(
		(error: Error, analyticsEvent?: UIAnalyticsEvent) => {
			fireErrorAnalytics({
				meta: {
					id: 'loadProfileCardClient',
					packageName: 'jiraProfilecardProvider',
					teamName: 'bento',
				},
				error,
				// Skipping sentry since there is no follow-up action for these errors.
				skipSentry: true,
			});

			providedOnLoadError && providedOnLoadError(error, analyticsEvent);
		},
		[providedOnLoadError],
	);

	const profileClient = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			createUserProfileCardClient(
				fabricPfDirUrl || DEFAULT_PROFILE_DIR_URL,
				baseUrl,
				onLoadError,
				teamCentralUrl,
				teamCentralBaseUrl,
				GATEWAY_GRAPHQL_URL,
				cloudId,
			) as ProfileClient,
		[fabricPfDirUrl, onLoadError, teamCentralUrl, teamCentralBaseUrl, cloudId],
	);

	const getActions = useCallback(
		// @ts-expect-error - TS2345 - Argument of type 'string[]' is not assignable to parameter of type 'Actions[]'.
		(id: string) => getSpaActions(id, baseUrl, intl, push, actions),
		[actions, intl, push],
	);

	const provider = useMemo(
		() =>
			Promise.resolve({
				cloudId,
				getActions,
				resourceClient: profileClient,
			}),
		[cloudId, getActions, profileClient],
	);

	return provider;
};
